import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@repo/ui';
import { api } from '@xtnd/web/shared/api';
import { invoiceApi, juniperQueryApi, type InvoiceQueryParams } from '../api';

// Query keys
export const invoiceKeys = {
  all: ['invoices'] as const,
  lists: () => [...invoiceKeys.all, 'list'] as const,
  list: (filters: InvoiceQueryParams) => [...invoiceKeys.lists(), { filters }] as const,
  details: () => [...invoiceKeys.all, 'detail'] as const,
  detail: (id: string) => [...invoiceKeys.details(), id] as const,
  deliverySummary: () => [...invoiceKeys.all, 'deliverySummary'] as const,
  deliveryDetails: () => [...invoiceKeys.all, 'deliveryDetails'] as const,
  deliveryDetail: (id: string) => [...invoiceKeys.deliveryDetails(), id] as const,
  documentHistory: () => [...invoiceKeys.all, 'documentHistory'] as const,
  documentHistoryById: (id: string) => [...invoiceKeys.documentHistory(), id] as const,
};

/**
 * Query hook for fetching invoices list with filtering and pagination
 */
export function useInvoices(params: InvoiceQueryParams = {}) {
  return useQuery({
    queryKey: invoiceKeys.list(params),
    queryFn: async () => {
      const response = await invoiceApi.getInvoices(params);
      const transformedInvoices = invoiceApi.transformInvoices(response.data);
      const summary = response.summary || {
        bucketCounts: {},
        operationalStateCounts: {},
        trustRequestStateCounts: {},
        juniperStatusCounts: {},
        matterTypeCounts: {},
      };
      const pageInfo = response.pageInfo || {
        nextCursor: response.nextCursor,
        hasMore: response.hasMore,
        limit: response.limit,
        currentCount: transformedInvoices.length,
        totalMatching: transformedInvoices.length,
      };

      return {
        data: transformedInvoices,
        nextCursor: pageInfo.nextCursor,
        hasMore: pageInfo.hasMore,
        limit: pageInfo.limit,
        pageInfo,
        summary,
        metadata: response.metadata,
        statusCounts: summary.bucketCounts,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - invoices are slow-changing
  });
}

/**
 * Query hook for fetching a single invoice by Juniper ID
 */
export function useInvoice(juniperId: string) {
  return useQuery({
    queryKey: invoiceKeys.detail(juniperId),
    queryFn: async () => {
      const transaction = await invoiceApi.getInvoice(juniperId);
      return invoiceApi.transformInvoice(transaction);
    },
    enabled: !!juniperId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useTransactionDeliverySummary(juniperIds: string[]) {
  const normalizedJuniperIds = Array.from(
    new Set(juniperIds.filter(Boolean).map(id => id.trim()))
  ).sort();

  return useQuery({
    queryKey: [...invoiceKeys.deliverySummary(), normalizedJuniperIds.join(',')],
    queryFn: () => invoiceApi.getDeliverySummary(normalizedJuniperIds),
    enabled: normalizedJuniperIds.length > 0,
    staleTime: 60 * 1000,
  });
}

export function useTransactionDeliveryDetails(juniperId: string, enabled = true) {
  return useQuery({
    queryKey: invoiceKeys.deliveryDetail(juniperId),
    queryFn: () => invoiceApi.getDeliveryDetails(juniperId),
    enabled: enabled && !!juniperId,
    staleTime: 60 * 1000,
  });
}

export function useTransactionDocumentHistory(juniperId: string, enabled = true) {
  return useQuery({
    queryKey: invoiceKeys.documentHistoryById(juniperId),
    queryFn: () => invoiceApi.getTransactionDocumentHistory(juniperId),
    enabled: enabled && !!juniperId,
    staleTime: 60 * 1000,
  });
}

/**
 * Mutation for releasing held email for a single invoice
 */
export function useReleaseEmailMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ juniperId, tenantId, emailTemplateOverrideId }: { juniperId: string; tenantId: string; emailTemplateOverrideId?: string }) => {
      const requestId = `release-${juniperId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return api.post(
        `/juniper/transactions/${juniperId}/release`,
        {
          juniperId,
          tenantId,
          requestId,
          requestType: 'release',
          reason: 'Manual email release from juniper transactions page',
          emailTemplateOverrideId,
        },
        {
          headers: {
            'X-Request-Timeout': '60000',
          },
        }
      );
    },
    onSuccess: (response: any) => {
      if (response.message) {
        showToast.success(response.message);
      } else {
        showToast.success('Email release queued successfully - check back in a few moments...');
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.error || error?.message || 'Failed to queue email release. Please try again.'
      );
    },
  });
}

/**
 * Mutation for sending follow-up email for a single invoice
 */
export function useResendEmailMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ juniperId, tenantId, emailTemplateOverrideId }: { juniperId: string; tenantId: string; emailTemplateOverrideId?: string }) => {
      const requestId = `follow-up-${juniperId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return api.post(
        `/juniper/transactions/${juniperId}/send-follow-up`,
        {
          juniperId,
          tenantId,
          requestId,
          requestType: 'follow_up',
          reason: 'Manual follow-up from juniper transactions page',
          emailTemplateOverrideId,
        },
        {
          headers: {
            'X-Request-Timeout': '60000',
          },
        }
      );
    },
    onSuccess: (response: any) => {
      if (response.message) {
        showToast.success(response.message);
      } else {
        showToast.success('Follow-up email queued successfully - check back in a few moments...');
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.error || error?.message || 'Failed to queue follow-up email. Please try again.'
      );
    },
  });
}

/**
 * Mutation for bulk sending emails for multiple invoices
 */
export function useBulkSendEmailsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ juniperIds, emailTemplateOverrideId }: { juniperIds: string[]; emailTemplateOverrideId?: string }) => {
      const requestId = `bulk-send-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return api.post('/juniper/transactions/bulk-send', {
        juniperIds,
        requestId,
        reason: 'Bulk email send from juniper transactions page',
        emailTemplateOverrideId,
      });
    },
    onSuccess: (response: any) => {
      const total = response?.total || 0;
      if (total > 0) {
        showToast.success(`${total} email(s) queued for sending - check back in a few moments...`);
      } else if (response?.status === 'complete') {
        showToast.info('No transactions to send');
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.error ||
        error?.message ||
        'Failed to queue bulk emails. Please try again.'
      );
    },
  });
}

/**
 * Mutation for regenerating PDF for a single invoice
 */
export function useRegeneratePdfMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (juniperId: string) => {
      const requestId = `pdf-regen-${juniperId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return api.post(
        `/juniper/transactions/${juniperId}/regenerate-pdf`,
        {
          requestId,
          requestType: 'regenerate_pdf',
          reason: 'Manual PDF regeneration from juniper transactions page',
        },
        {
          headers: {
            'X-Request-Timeout': '60000',
          },
        }
      );
    },
    onSuccess: (response: any) => {
      if (response.message) {
        showToast.success(response.message);
      } else {
        showToast.success('PDF regeneration queued - check back in a few moments...');
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to queue PDF regeneration. Please try again.'
      );
    },
  });
}

/**
 * Mutation for bulk PDF regeneration for multiple invoices
 */
export function useBulkRegeneratePdfMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (juniperIds: string[]) => {
      const requestId = `bulk-pdf-regen-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return api.post('/juniper/transactions/bulk-regenerate-pdf', {
        juniperIds,
        requestId,
        reason: 'Bulk PDF regeneration from juniper transactions page',
      });
    },
    onSuccess: (response: any) => {
      const total = response?.total || 0;
      if (total > 0) {
        showToast.success(`${total} PDF(s) queued for regeneration - check back in a few moments...`);
      } else if (response?.status === 'complete') {
        showToast.info('No PDFs to regenerate');
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.error ||
        error?.message ||
        'Failed to queue bulk PDF regeneration. Please try again.'
      );
    },
  });
}

/**
 * Mutation for bulk consolidating selected Email Held PDFs for printing.
 * Returns the accept-then-dispatch response (`{ jobId, total, status: 'queued' }`).
 * The caller is expected to feed `jobId` into `BulkJobProgressTracker`, which
 * polls and opens the merged-PDF downloadUrl on completion.
 */
export function useBulkConsolidatePrintMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (juniperIds: string[]) => {
      const requestId = `bulk-consolidate-print-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return api.post('/juniper/transactions/bulk-consolidate-print', {
        juniperIds,
        requestId,
        reason: 'Bulk consolidate print from juniper transactions page',
      });
    },
    onSuccess: (response: any) => {
      const total = response?.total || 0;
      if (total > 0) {
        showToast.info(
          `${total} PDF(s) queued for consolidation - the merged PDF will open in a new tab when ready...`,
        );
      } else if (response?.status === 'complete') {
        showToast.info('No PDFs to consolidate');
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        '';

      if (msg.toLowerCase().includes('exceeds maximum') || msg.toLowerCase().includes('total source size')) {
        showToast.error('The selected PDFs are too large to consolidate. Please select fewer transactions and try again.');
      } else {
        showToast.error(msg || 'Failed to generate consolidated PDF. Please try again.');
      }
    },
  });
}

/**
 * Mutation for bulk refresh from Clio for multiple invoices
 */
export function useBulkRefreshFromClioMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (juniperIds: string[]) => {
      const requestId = `bulk-refresh-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return api.post('/juniper/transactions/bulk-reprocess', {
        juniperIds,
        requestId,
        reason: 'Bulk refresh from Clio from juniper transactions page',
      });
    },
    onSuccess: (response: any) => {
      const total = response?.total || 0;
      if (total > 0) {
        showToast.success(`${total} transaction(s) queued for refresh from Clio - check back in a few moments...`);
      } else if (response?.status === 'complete') {
        showToast.info('No transactions to refresh');
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.error ||
        error?.message ||
        'Failed to queue bulk refresh from Clio. Please try again.'
      );
    },
  });
}

/**
 * Mutation for bulk retrying the last webhook event for multiple transactions
 * by Juniper ID. Routes through the bulk-job acceptance system (accept-then-
 * dispatch, idKind='juniper') so it doesn't hit the 30-second API Gateway
 * timeout. Returns immediately with { jobId, total, status: 'queued' }.
 */
export function useBulkSimulateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      juniperIds: string[];
      billType: 'operational' | 'trust';
      eventType: 'update' | 'delete';
      reason?: string;
    }) => {
      const requestId = `bulk-simulate-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return api.post('/juniper/transactions/bulk-simulate', {
        ...params,
        requestId,
      });
    },
    onSuccess: (response: any) => {
      const { total, jobId } = response || {};
      if (total > 0) {
        showToast.success(
          `${total} last event${total > 1 ? 's' : ''} queued for retry - check back in a few moments...`,
        );
      } else if (jobId === '') {
        showToast.info('No transactions to retry');
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to queue retry last events. Please try again.'
      );
    },
  });
}

/**
 * Mutation for bulk void/delete of trust request bills for Evergreen transactions
 */
export function useBulkVoidDeleteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      juniperIds: string[];
      requestId: string;
      reason?: string;
    }) => {
      const response = await api.post('/juniper/transactions/bulk-void-delete', params);
      return response;
    },
    onSuccess: (response: any) => {
      const total = response?.total || 0;
      if (total > 0) {
        showToast.success(`${total} trust request(s) queued for void/delete - state will update after Clio processes the change...`);
      } else if (response?.status === 'complete') {
        showToast.info('No trust requests to void/delete');
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.error ||
        error?.message ||
        'Failed to queue void/delete. Please try again.'
      );
    },
  });
}

export function useMarkCompletedMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ juniperId }: { juniperId: string }) => {
      const requestId = `mark-completed-${juniperId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return api.post(`/juniper/transactions/${juniperId}/mark-completed`, {
        requestId,
        reason: 'Manual mark completed from juniper transactions page',
      });
    },
    onSuccess: (response: any) => {
      showToast.success(response?.message || 'Transaction marked as completed');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.error ||
        error?.message ||
        'Failed to mark as completed. Please try again.'
      );
    },
  });
}

export function useBulkMarkCompletedMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      juniperIds: string[];
      requestId: string;
      reason?: string;
    }) => {
      return api.post('/juniper/transactions/bulk-mark-completed', params);
    },
    onSuccess: (response: any) => {
      const total = response?.total || 0;
      if (total > 0) {
        showToast.success(`${total} transaction(s) queued to mark as completed - check back in a few moments...`);
      } else if (response?.status === 'complete') {
        showToast.info('No transactions to mark as completed');
      }
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (error: any) => {
      showToast.error(
        error?.response?.data?.error ||
        error?.message ||
        'Failed to mark as completed. Please try again.'
      );
    },
  });
}
